import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Package, ClipboardList, QrCode, BarChart3,
  Images, Settings, Users, LogOut, Loader2, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DashboardHome from "@/components/admin/DashboardHome";
import ProductsManager from "@/components/admin/ProductsManager";
import SurveyManager from "@/components/admin/SurveyManager";
import DistributorsManager from "@/components/admin/DistributorsManager";
import StatsPanel from "@/components/admin/StatsPanel";
import MediaManager from "@/components/admin/MediaManager";
import SettingsManager from "@/components/admin/SettingsManager";
import AdminsManager from "@/components/admin/AdminsManager";

const NAV = [
  { key: "dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["super_admin", "editor", "analytics"] },
  { key: "products", icon: Package, label: "Produits", roles: ["super_admin", "editor"] },
  { key: "survey", icon: ClipboardList, label: "Sondage", roles: ["super_admin", "editor"] },
  { key: "machines", icon: QrCode, label: "Distributeurs", testid: "nav-distributors", roles: ["super_admin", "editor", "analytics"] },
  { key: "stats", icon: BarChart3, label: "Statistiques", roles: ["super_admin", "editor", "analytics"] },
  { key: "media", icon: Images, label: "Médias", roles: ["super_admin", "editor"] },
  { key: "settings", icon: Settings, label: "Paramètres", roles: ["super_admin"] },
  { key: "admins", icon: Users, label: "Administrateurs", roles: ["super_admin"] },
];

const ROLE_LABEL = { super_admin: "Super Admin", editor: "Éditeur", analytics: "Analytics" };

export default function AdminDashboard() {
  const { user, logout, role } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user === false || (user && !user.email)) navigate("/admin/login");
  }, [user, navigate]);

  if (user === null) {
    return <div className="flex min-h-[100dvh] items-center justify-center bg-caneat-pageCream"><Loader2 className="h-8 w-8 animate-spin text-caneat-orange" /></div>;
  }
  if (!user || !user.email) return null;

  const items = NAV.filter((n) => n.roles.includes(role));
  const active = items.find((i) => i.key === section) ? section : "dashboard";
  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const render = () => {
    switch (active) {
      case "dashboard": return <DashboardHome onGoto={setSection} />;
      case "products": return <ProductsManager />;
      case "survey": return <SurveyManager />;
      case "machines": return <DistributorsManager />;
      case "stats": return <StatsPanel />;
      case "media": return <MediaManager />;
      case "settings": return <SettingsManager />;
      case "admins": return <AdminsManager />;
      default: return <DashboardHome onGoto={setSection} />;
    }
  };

  const NavList = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <button
          key={it.key}
          data-testid={it.testid || `nav-${it.key}`}
          onClick={() => { setSection(it.key); onClick && onClick(); }}
          className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-bold transition-colors ${
            active === it.key ? "bg-caneat-orange text-white shadow-sm" : "text-caneat-ink hover:bg-caneat-pageCream"
          }`}
        >
          <it.icon className="h-[18px] w-[18px]" /> {it.label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-[100dvh] bg-caneat-pageCream">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-[100dvh] w-64 shrink-0 flex-col border-r border-caneat-cream bg-white px-4 py-5 lg:flex">
        <div className="mb-6 flex items-center gap-2 px-2">
          <img src="/brand/caneat-logo-orange.png" alt="CAN'EAT" className="h-9 w-auto" />
          <span className="text-xs font-extrabold uppercase tracking-widest text-caneat-muted">Admin</span>
        </div>
        <NavList />
        <div className="mt-auto rounded-xl bg-caneat-pageCream p-3">
          <p className="truncate text-sm font-bold text-caneat-ink">{user.name}</p>
          <p className="truncate text-xs text-caneat-muted">{ROLE_LABEL[role] || role}</p>
          <button onClick={doLogout} data-testid="admin-logout"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-caneat-ink py-2 text-xs font-bold text-white">
            <LogOut className="h-3.5 w-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-caneat-cream bg-white px-4 py-3 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/brand/caneat-logo-orange.png" alt="CAN'EAT" className="h-8 w-auto" />
            <span className="text-xs font-extrabold uppercase tracking-widest text-caneat-muted">Admin</span>
          </div>
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" className="flex h-9 w-9 items-center justify-center rounded-lg bg-caneat-pageCream">
            <Menu className="h-5 w-5 text-caneat-ink" />
          </button>
        </header>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div className="fixed inset-0 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
              <motion.div initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
                className="absolute left-0 top-0 h-full w-72 bg-white px-4 py-5">
                <div className="mb-5 flex items-center justify-between">
                  <img src="/brand/caneat-logo-orange.png" alt="CAN'EAT" className="h-9 w-auto" />
                  <button onClick={() => setMobileOpen(false)}><X className="h-6 w-6 text-caneat-ink" /></button>
                </div>
                <NavList onClick={() => setMobileOpen(false)} />
                <button onClick={doLogout}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-caneat-ink py-2.5 text-sm font-bold text-white">
                  <LogOut className="h-4 w-4" /> Déconnexion
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {render()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
